/**
 * The viewer's remembered time preset, so the page stays on the window they
 * chose instead of snapping back to {@link DEFAULT_PRESET} every time they
 * leave the tab and come back.
 *
 * Three shape decisions are load-bearing:
 *
 * * **Not a Zustand store.** Nothing renders from this value: the URL remains
 *   the single source of truth for what the page draws, and this is consulted
 *   exactly once per navigation, in `beforeLoad`, to decide what that URL
 *   should say. A store would buy a subscription no component wants.
 * * **Presets only, never a custom range.** A preset is relative and always
 *   means something; `?from=2026-07-01&to=2026-07-14` restored three weeks
 *   later is a stale window nobody asked for, and may be refused outright by
 *   the endpoint's span limit. Choosing a custom range therefore leaves the
 *   remembered preset untouched — coming back lands on the last *preset*.
 * * **One global key.** A time window says nothing about a tenant, so there is
 *   nothing to scope it to.
 *
 * `localStorage` rather than `sessionStorage`: "I look at this dashboard over
 * 30 days" is a preference, not a property of one tab.
 */
import { DEFAULT_PRESET, isOverviewPreset } from './window'

import type { OverviewSearch } from './search-schema'
import type { OverviewPreset } from './window'

const STORAGE_KEY = '@bitlyft/air-overview-preset'

/**
 * The remembered preset, or `null` when there is none to honour.
 *
 * Anything unreadable — a retired token, a hand-edited value, or a storage that
 * throws on access because the browser has disabled it — reads as "no
 * preference" rather than propagating. This runs inside `beforeLoad`, where a
 * throw is a failed navigation; forgetting a preference is the only acceptable
 * failure mode for it.
 */
export function readPreferredPreset(): OverviewPreset | null {
	try {
		const stored = localStorage.getItem(STORAGE_KEY)
		return isOverviewPreset(stored) ? stored : null
	} catch {
		return null
	}
}

/**
 * The search a bare `/air/overview` should be redirected to, or `null` to let
 * the URL stand.
 *
 * The route throws this as a redirect; the decision lives here because it is
 * the rule, not the plumbing, and a route builds nothing (A-01).
 *
 * Two conditions, both narrow on purpose:
 *
 * * **Every window param must be absent.** A URL that says anything about the
 *   window — a shared `?preset=7d`, a back button, a custom range — is already
 *   an answer and outranks what was remembered. This is also what makes the
 *   redirect terminate: what it returns always carries a preset.
 * * **The remembered preset must differ from the default.** Redirecting to the
 *   window the bare URL already resolves to would spend a navigation to change
 *   nothing, and would put `?preset=7d` on a URL the user never asked to
 *   decorate.
 */
export function restoredSearch(search: OverviewSearch): OverviewSearch | null {
	if (search.preset || search.from || search.to) return null

	const preferred = readPreferredPreset()
	if (!preferred || preferred === DEFAULT_PRESET) return null

	return { preset: preferred }
}

/**
 * Record a selection the filter bar has just made.
 *
 * A custom range is deliberately not recorded — and does not clear what is,
 * so the next bare visit lands on the last preset rather than on the default.
 * Storage that is disabled or full is a no-op: the URL still carries the
 * selection for this visit, so the only thing lost is its memory.
 */
export function rememberSelection(search: OverviewSearch): void {
	const { preset } = search
	if (!preset || preset === 'custom') return

	try {
		localStorage.setItem(STORAGE_KEY, preset)
	} catch {
		// See above — forgetting is the acceptable failure.
	}
}
